import { useEffect, useRef } from 'react';
import { getAuthPort } from '@/data/auth';
import { config } from '@/data/config';

/**
 * WebSocket de `criado`/`cancelado` em agendamentos.
 *
 * Fecha a lacuna que o polling só mitiga: duas pessoas olhando a mesma
 * instalação/horário só percebiam a mudança da outra no próximo ciclo (ou
 * nunca, sem recarregar a página). O backend (`/agendamentos/ws`, stable)
 * publica no Redis a cada criação/cancelamento; aqui só repassamos pro
 * `aoReceberEvento` do chamador, que decide o que refazer.
 *
 * Autenticação do handshake é por cookie (`access_token=Bearer <jwt>`) — o
 * `WebSocket` nativo do navegador não permite header customizado, e o
 * middleware Keycloak do backend já sabe ler esse cookie especificamente para
 * conexões WebSocket. Por isso o cookie é atualizado com um token fresco a
 * cada tentativa de conexão, não só uma vez.
 *
 * Em produção, front (`pan.nasajon.in`) e API (`api.nasajon.in`) são hosts
 * diferentes — sem `domain=`, esse cookie nunca sairia do host que o setou.
 * `domain=.nasajon.in` resolve isso (cobre os dois); em dev (localhost/proxy
 * do Vite, mesma origem) é omitido, não tem domínio comum pra usar e não
 * precisa.
 *
 * Já existiu aqui uma tentativa via `Sec-WebSocket-Protocol` (o token como
 * subprotocolo, com uma ponte no backend traduzindo de volta pra cookie) —
 * revertida: coincidiu com quedas abruptas (code 1006) da conexão em produção
 * que não reproduziram em nenhum teste isolado (Redis, proxy do Vite, a ponte
 * em si, tudo testado à parte, tudo estável). Não confirmamos a causa raiz,
 * mas a suspeita ficou forte o bastante pra não valer manter a complexidade.
 */

const BASE_RECONEXAO_MS = 1_000;
const TETO_RECONEXAO_MS = 30_000;

function paraWebSocket(url: string): string {
  if (url.startsWith('https://')) return `wss://${url.slice('https://'.length)}`;
  if (url.startsWith('http://')) return `ws://${url.slice('http://'.length)}`;
  const protocolo = window.location.protocol === 'https:' ? 'wss' : 'ws';
  return `${protocolo}://${window.location.host}${url}`;
}

function definirCookieToken(token: string): void {
  try {
    const seguro = window.location.protocol === 'https:' ? '; Secure' : '';
    const dominio = window.location.hostname.endsWith('nasajon.in') ? '; domain=.nasajon.in' : '';
    document.cookie = `access_token=Bearer ${token}; path=/; SameSite=Lax${dominio}${seguro}`;
  } catch {
    // Cookie bloqueado (aba anônima com storage restrito, política do
    // navegador): a conexão WS não autentica e cai no backoff normal — o
    // polling continua garantindo a atualização.
  }
}

/** Evento cru do canal — `tipo` sempre presente, o resto varia por tipo
 * (`instalacoes`, `holdId`, `horario`...). Ver `publicar_evento` no backend. */
export interface EventoAgendamento {
  tipo: string;
  [chave: string]: unknown;
}

export function useRealtimeAgendamentos(aoReceberEvento: (evento: EventoAgendamento) => void): void {
  const aoReceberEventoRef = useRef(aoReceberEvento);
  aoReceberEventoRef.current = aoReceberEvento;

  useEffect(() => {
    if (config.dataSource !== 'rest') return;

    let socket: WebSocket | null = null;
    let idReconexao: number | undefined;
    let tentativas = 0;
    let encerrado = false;

    async function conectar() {
      if (encerrado) return;

      let token: string | undefined;
      try {
        token = await getAuthPort().getToken();
        if (token) definirCookieToken(token);
      } catch {
        // Sem token fresco agora: tenta mesmo assim — se o cookie estiver
        // velho, o servidor recusa e cai no backoff de reconexão normal.
      }
      if (encerrado) return;

      const url = `${paraWebSocket(config.apiBaseUrl)}/agendamentos/ws`;
      socket = new WebSocket(url);

      socket.onopen = () => {
        tentativas = 0;
      };

      socket.onmessage = (evento) => {
        try {
          const payload = JSON.parse(evento.data as string) as EventoAgendamento;
          if (payload?.tipo && payload.tipo !== 'ping') aoReceberEventoRef.current(payload);
        } catch {
          // Mensagem não-JSON: ignora.
        }
      };

      socket.onclose = () => {
        if (encerrado) return;
        const atraso = Math.min(BASE_RECONEXAO_MS * 2 ** tentativas, TETO_RECONEXAO_MS);
        tentativas += 1;
        idReconexao = window.setTimeout(conectar, atraso);
      };

      socket.onerror = () => {
        socket?.close();
      };
    }

    conectar();

    return () => {
      encerrado = true;
      if (idReconexao !== undefined) window.clearTimeout(idReconexao);
      socket?.close();
    };
  }, []);
}
